import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { API_BASE } from "../../config/api";
import Pagination from "../../Components/Pagination";
import DiscountActiveStatus from "../../Components/DiscountActiveStatus";

const PAGE_SIZE = 25;

interface Discount {
  discount_id: number;
  user_id: number;
  user_name?: string;
  user_email?: string;
  // store_id: nullable — an Admin/Sales-created discount is auto-attributed
  // to the store they're scoped to (or the one they explicitly picked, if
  // they own more than one); an Owner-created or otherwise-unattributed
  // discount has no store boundary at all, same as an unattributed Coupon.
  store_id: number | null;
  store_name?: string | null;
  discount_name: string;
  discount_type: string;
  discount_value: number;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  is_currently_active: boolean;
  created_at: string;
}

interface EditDiscount {
  discount_id?: number;
  user_id: string;
  store_id: string;
  discount_name: string;
  discount_type: string;
  discount_value: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
}

interface User {
  user_id: number;
  user_first_name: string;
  user_last_name: string;
  email: string;
}

interface Store {
  store_id: number;
  store_name: string;
}

const DiscountPage = () => {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedDiscounts, setSelectedDiscounts] = useState<number[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editDiscount, setEditDiscount] = useState<EditDiscount>({
    user_id: "",
    store_id: "",
    discount_name: "",
    discount_type: "percentage",
    discount_value: "",
    valid_from: "",
    valid_until: "",
    is_active: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();

  useEffect(() => {
    fetchUsers();
    fetchStores();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const fetchDiscounts = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: PAGE_SIZE.toString() });
      if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/discount?${params.toString()}`, { method: "GET" }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setDiscounts(Array.isArray(data?.data) ? data.data : []);
        setTotalPages(data?.totalPages || 1);
        setTotalItems(data?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching discounts:", error);
    }
  }, [cookies.auth, page, debouncedSearchTerm]);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  const fetchUsers = useCallback(async () => {
    try {
      // getAllUsers (app_be/controllers/userController.js) excludes Customers
      // by default (whereClause.user_type = { [Op.ne]: 'C' } — it's built for
      // the Owner/Admin staff-management screen, where Customers deliberately
      // never show). This is a user-level *Discount*, though — the whole
      // point is granting one to a specific shopper — so it needs the
      // opposite scope: explicitly ask for user_type=C, otherwise this
      // dropdown silently listed only the handful of O/A/S accounts (or came
      // back empty if none existed to see), never the actual customers being
      // granted a discount.
      const response = await authenticatedFetch(`${API_BASE}/user/users?page=1&limit=100&user_type=C`, { method: "GET" }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        const arr = Array.isArray(data) ? data : (data?.users || []);
        setUsers(arr);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [cookies.auth]);

  const fetchStores = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/store?page=1&limit=100`, { method: "GET" }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setStores(Array.isArray(data?.data) ? data.data : []);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
    }
  }, [cookies.auth]);

  const handleAdd = () => {
    setEditDiscount({ user_id: "", store_id: "", discount_name: "", discount_type: "percentage", discount_value: "", valid_from: "", valid_until: "", is_active: true });
    setShowAddModal(true);
  };

  const handleUpdate = () => {
    if (selectedDiscounts.length !== 1) { alert("Please select exactly one discount to update"); return; }
    const d = discounts.find(x => x.discount_id === selectedDiscounts[0]);
    if (d) {
      setEditDiscount({
        discount_id: d.discount_id,
        user_id: d.user_id.toString(),
        store_id: d.store_id ? d.store_id.toString() : "",
        discount_name: d.discount_name || "",
        discount_type: d.discount_type || "percentage",
        discount_value: d.discount_value?.toString() || "",
        valid_from: d.valid_from ? d.valid_from.slice(0, 10) : "",
        valid_until: d.valid_until ? d.valid_until.slice(0, 10) : "",
        is_active: d.is_active,
      });
      setShowEditModal(true);
    }
  };

  const handleDelete = () => {
    if (selectedDiscounts.length === 0) { alert("Please select at least one discount to delete"); return; }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      for (const id of selectedDiscounts) {
        await authenticatedFetch(`${API_BASE}/discount/${id}`, { method: "DELETE" }, cookies.auth);
      }
      setSelectedDiscounts([]);
      setShowDeleteConfirm(false);
      await fetchDiscounts();
      snack.onResponse({ message: "Discount(s) deleted successfully!", status: 200 });
    } catch (error) {
      snack.onResponse({ message: "Error deleting discount(s)", status: 500 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (isEdit: boolean) => {
    try {
      setIsLoading(true);
      if (!editDiscount.user_id) { alert("Please select a user"); return; }
      if (!editDiscount.discount_name.trim()) { alert("Discount name is required"); return; }
      if (!editDiscount.discount_value) { alert("Discount value is required"); return; }

      const payload = {
        user_id: parseInt(editDiscount.user_id),
        // Sent explicitly (even as null for "no store selected") rather
        // than omitted — the backend's auto-assign-from-the-caller's-
        // single-owned-store logic only triggers on a falsy store_id, so
        // an explicit null doesn't defeat it; omitting the key on edit
        // would instead leave the previous store_id untouched, which
        // isn't what re-saving a cleared dropdown should do.
        store_id: editDiscount.store_id ? parseInt(editDiscount.store_id) : null,
        discount_name: editDiscount.discount_name.trim(),
        discount_type: editDiscount.discount_type,
        discount_value: parseFloat(editDiscount.discount_value),
        valid_from: editDiscount.valid_from || null,
        valid_until: editDiscount.valid_until || null,
        is_active: editDiscount.is_active,
      };

      const url = isEdit ? `${API_BASE}/discount/${editDiscount.discount_id}` : `${API_BASE}/discount`;
      const method = isEdit ? "PUT" : "POST";

      const response = await authenticatedFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }, cookies.auth);

      if (response.ok) {
        setShowEditModal(false);
        setShowAddModal(false);
        setSelectedDiscounts([]);
        await fetchDiscounts();
        snack.onResponse({ message: `Discount ${isEdit ? "updated" : "created"} successfully!`, status: 200 });
      } else {
        const err = await response.text();
        snack.onResponse({ message: `Failed to save discount: ${err}`, status: response.status });
      }
    } catch (error) {
      snack.onResponse({ message: "Error saving discount", status: 500 });
    } finally {
      setIsLoading(false);
    }
  };

  const getUserName = (userId: number) => {
    const u = users.find(x => x.user_id === userId);
    return u ? `${u.user_first_name} ${u.user_last_name}` : `User ${userId}`;
  };

  const getStoreName = (storeId: number | null) => {
    if (storeId == null) return "—";
    const s = stores.find(x => x.store_id === storeId);
    return s ? s.store_name : `Store ${storeId}`;
  };

  const formatDate = (d: string | null | undefined) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
  };

  const filteredDiscounts = discounts;

  const closeModals = () => { setShowAddModal(false); setShowEditModal(false); };

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <h1 className={style.title}>User Discounts</h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search by discount name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          {permissions.canAdd && (
            <div className={style.actionButtons}>
              <button className={style.actionBtn} onClick={handleAdd}>Add</button>
              <button className={style.actionBtn} onClick={handleUpdate}>Update</button>
              <button className={style.actionBtnDanger} onClick={handleDelete}>Delete</button>
            </div>
          )}
        </div>
      </div>

      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {permissions.canEdit && <th><input type="checkbox" checked={selectedDiscounts.length === discounts.length && discounts.length > 0} onChange={e => setSelectedDiscounts(e.target.checked ? discounts.map(d => d.discount_id) : [])} className={style.checkbox} /></th>}
              <th>ID</th>
              <th>User</th>
              <th>Store</th>
              <th>Name</th>
              <th>Type</th>
              <th>Value</th>
              <th>Valid From</th>
              <th>Valid Until</th>
              <th>Active</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {filteredDiscounts.map(d => (
              <tr key={d.discount_id} className={style.tableRow}>
                {permissions.canEdit && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedDiscounts.includes(d.discount_id)}
                      onChange={e => setSelectedDiscounts(e.target.checked ? [d.discount_id] : selectedDiscounts.filter(id => id !== d.discount_id))}
                      className={style.checkbox}
                    />
                  </td>
                )}
                <td>{d.discount_id}</td>
                <td>{d.user_name || getUserName(d.user_id)}</td>
                <td>{d.store_name || getStoreName(d.store_id)}</td>
                <td>{d.discount_name || "—"}</td>
                <td>
                  <span className={d.discount_type === "percentage" ? style.badgePercent : style.badgeFlat}>
                    {d.discount_type || "—"}
                  </span>
                </td>
                <td>{d.discount_type === "percentage" ? `${d.discount_value}%` : `PKR ${d.discount_value}`}</td>
                <td>{formatDate(d.valid_from)}</td>
                <td>{formatDate(d.valid_until)}</td>
                <td><DiscountActiveStatus isCurrentlyActive={d.is_currently_active} /></td>
                <td>{formatDate(d.created_at)}</td>
              </tr>
            ))}
            {filteredDiscounts.length === 0 && (
              <tr><td colSpan={11} className={style.emptyState}>No discounts found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />

      {(showAddModal || showEditModal) && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>{showAddModal ? "Add Discount" : "Edit Discount"}</h2>
            <div className={style.formGroup}>
              <label>User *</label>
              <select value={editDiscount.user_id} onChange={e => setEditDiscount({ ...editDiscount, user_id: e.target.value })} className={style.modalInput}>
                <option value="">Select user...</option>
                {users.map(u => (
                  <option key={u.user_id} value={u.user_id}>{u.user_first_name} {u.user_last_name} ({u.email})</option>
                ))}
              </select>
            </div>
            <div className={style.formGroup}>
              <label>Store</label>
              <select value={editDiscount.store_id} onChange={e => setEditDiscount({ ...editDiscount, store_id: e.target.value })} className={style.modalInput}>
                <option value="">No specific store</option>
                {stores.map(s => (
                  <option key={s.store_id} value={s.store_id}>{s.store_name}</option>
                ))}
              </select>
            </div>
            <div className={style.formGroup}>
              <label>Discount Name *</label>
              <input type="text" value={editDiscount.discount_name} onChange={e => setEditDiscount({ ...editDiscount, discount_name: e.target.value })} className={style.modalInput} placeholder="e.g. VIP Customer Discount" />
            </div>
            <div className={style.formGroup}>
              <label>Type *</label>
              <select value={editDiscount.discount_type} onChange={e => setEditDiscount({ ...editDiscount, discount_type: e.target.value })} className={style.modalInput}>
                <option value="percentage">Percentage (%)</option>
                <option value="flat">Flat (PKR)</option>
              </select>
            </div>
            <div className={style.formGroup}>
              <label>Value *</label>
              <input type="number" min="0" step="0.01" value={editDiscount.discount_value} onChange={e => setEditDiscount({ ...editDiscount, discount_value: e.target.value })} className={style.modalInput} placeholder="e.g. 10" />
            </div>
            <div className={style.formGroup}>
              <label>Valid From</label>
              <input type="date" value={editDiscount.valid_from} onChange={e => setEditDiscount({ ...editDiscount, valid_from: e.target.value })} className={style.modalInput} />
            </div>
            <div className={style.formGroup}>
              <label>Valid Until</label>
              <input type="date" value={editDiscount.valid_until} onChange={e => setEditDiscount({ ...editDiscount, valid_until: e.target.value })} className={style.modalInput} />
            </div>
            <div className={style.formGroup}>
              <label>
                <input
                  type="checkbox"
                  checked={editDiscount.is_active}
                  onChange={e => setEditDiscount({ ...editDiscount, is_active: e.target.checked })}
                />
                {' '}Active (uncheck to pause this discount without changing its dates)
              </label>
            </div>
            <div className={style.modalButtons}>
              <button onClick={closeModals} className={style.cancelBtn} disabled={isLoading}>Cancel</button>
              <button onClick={() => handleSave(showEditModal)} className={style.saveBtn} disabled={isLoading}>
                {isLoading ? "Saving..." : (showAddModal ? "Add" : "Update")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Confirm Delete</h2>
            <p>Delete {selectedDiscounts.length} discount(s)?</p>
            <div className={style.modalButtons}>
              <button onClick={() => setShowDeleteConfirm(false)} className={style.cancelBtn} disabled={isLoading}>Cancel</button>
              <button onClick={confirmDelete} className={style.deleteBtn} disabled={isLoading}>{isLoading ? "Deleting..." : "Delete"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountPage;
